/**
 * 安卓触屏控制：左半屏虚拟摇杆 + 右下角「坐/站」。
 *
 * 注意两件事（架构 §4.7）：
 *  - 触摸端**没有 Pointer Lock**，视角靠拖动；摇杆放在独立元素上，
 *    这样在摇杆上拖动不会同时转动视角；
 *  - 目标尺寸必须够大（≥44 px），所以摇杆半径按视口短边的比例算。
 */

export interface TouchHandlers {
  onMove(forward: number, strafe: number): void;
  onSit(): void;
}

const DEADZONE = 0.15;

export function mountTouchControls(handlers: TouchHandlers): () => void {
  const pad = document.createElement('div');
  pad.id = 'joystick';
  pad.setAttribute('aria-label', '移动摇杆');

  const knob = document.createElement('div');
  knob.className = 'knob';
  pad.appendChild(knob);

  const sitBtn = document.createElement('button');
  sitBtn.id = 'sit-btn';
  sitBtn.type = 'button';
  sitBtn.textContent = '坐 / 站';

  document.body.append(pad, sitBtn);

  let activeId: number | null = null;

  const setKnob = (dx: number, dy: number): void => {
    knob.style.transform = `translate(${dx}px, ${dy}px)`;
  };

  const update = (e: PointerEvent): void => {
    const rect = pad.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const radius = rect.width / 2 - 12;

    let dx = e.clientX - cx;
    let dy = e.clientY - cy;
    const dist = Math.hypot(dx, dy);
    if (dist > radius) {
      dx = (dx / dist) * radius;
      dy = (dy / dist) * radius;
    }
    setKnob(dx, dy);

    // 屏幕坐标 → 船局部：上 = 前
    const forward = -dy / radius;
    const strafe = dx / radius;
    handlers.onMove(Math.abs(forward) < DEADZONE ? 0 : forward, Math.abs(strafe) < DEADZONE ? 0 : strafe);
  };

  const onDown = (e: PointerEvent): void => {
    e.preventDefault();
    activeId = e.pointerId;
    pad.setPointerCapture?.(e.pointerId);
    pad.classList.add('active');
    update(e);
  };

  const onMove = (e: PointerEvent): void => {
    if (activeId !== e.pointerId) return;
    e.preventDefault();
    update(e);
  };

  const onUp = (e: PointerEvent): void => {
    if (activeId !== e.pointerId) return;
    activeId = null;
    pad.classList.remove('active');
    setKnob(0, 0);
    handlers.onMove(0, 0);
  };

  pad.addEventListener('pointerdown', onDown);
  pad.addEventListener('pointermove', onMove);
  pad.addEventListener('pointerup', onUp);
  pad.addEventListener('pointercancel', onUp);

  const onSit = (e: PointerEvent): void => {
    e.preventDefault();
    handlers.onSit();
  };
  sitBtn.addEventListener('pointerdown', onSit);

  return () => {
    pad.remove();
    sitBtn.remove();
  };
}
